import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { recipesAPI } from '../../services/api';
import CategoryPicker from '../common/CategoryPicker';
import RichTextEditor from '../common/RichTextEditor';
import { FOOD_CATEGORIES, AVOID_CATEGORIES, CLASSIFICATION_CATEGORIES, CATEGORY_ICONS } from '../../services/constants';
import './AddRecipe.css';

export default function EditRecipePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // AI Review state
  const [reviewStatus, setReviewStatus] = useState('idle');
  const [reviewFeedback, setReviewFeedback] = useState('');
  const [reviewAttempts, setReviewAttempts] = useState(0);
  const MAX_REVIEW_ATTEMPTS = 3;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [recipeHtml, setRecipeHtml] = useState('');
  const [notes, setNotes] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [categories, setCategories] = useState([]);
  const [avoids, setAvoids] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [newPreviews, setNewPreviews] = useState([]);

  useEffect(() => {
    recipesAPI.getOne(id).then(data => {
      const r = data.recipe;
      setName(r.name);
      setDescription(r.description);
      setRecipeHtml(r.recipeHtml || '');
      setNotes(r.notes || '');
      setIsPublic(r.isPublic);
      setCategories(r.categories || []);
      setAvoids(r.avoids || []);
      setClassifications(r.classifications || []);
      setExistingImages(r.images || []);
    }).catch(() => navigate('/'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const handleNewImages = (e) => {
    const totalSlots = 3 - existingImages.length - newImages.length;
    const files = Array.from(e.target.files).slice(0, totalSlots);
    setNewImages(prev => [...prev, ...files]);
    files.forEach(f => {
      const reader = new FileReader();
      reader.onload = (ev) => setNewPreviews(prev => [...prev, ev.target.result]);
      reader.readAsDataURL(f);
    });
  };

  const removeNewImage = (idx) => {
    setNewImages(prev => prev.filter((_, i) => i !== idx));
    setNewPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const handleDeleteExisting = async (imageId) => {
    try {
      await recipesAPI.deleteImage(id, imageId);
      setExistingImages(prev => prev.filter(img => img.imageId !== imageId));
    } catch { /* ignore */ }
  };

  const toggle = (list, setList, item) => {
    setList(list.includes(item) ? list.filter(i => i !== item) : [...list, item]);
  };

  const handleSave = async () => {
    setError('');
    if (name.length < 4 || name.length > 20) { setError('Name must be 4-20 characters'); return; }
    if (description.length < 10 || description.length > 45) { setError('Description must be 10-45 characters'); return; }

    if (reviewAttempts >= MAX_REVIEW_ATTEMPTS) {
      setReviewStatus('maxFailed');
      return;
    }

    // Step 1: AI Review
    setReviewStatus('reviewing');
    setReviewFeedback('');
    try {
      const reviewResult = await recipesAPI.review({
        name, description, recipeHtml, categories, classifications, notes,
      });

      if (!reviewResult.approved) {
        const attempts = reviewAttempts + 1;
        setReviewAttempts(attempts);
        setReviewFeedback(reviewResult.feedback || 'Please review your recipe and try again.');
        if (attempts >= MAX_REVIEW_ATTEMPTS) {
          setReviewStatus('maxFailed');
        } else {
          setReviewStatus('rejected');
        }
        return;
      }
      setReviewStatus('approved');
    } catch (err) {
      setReviewFeedback('Review service is temporarily unavailable. Please try again.');
      setReviewStatus('rejected');
      const attempts = reviewAttempts + 1;
      setReviewAttempts(attempts);
      if (attempts >= MAX_REVIEW_ATTEMPTS) setReviewStatus('maxFailed');
      return;
    }

    // Step 2: Save
    setSaving(true);
    try {
      await recipesAPI.update(id, { name, description, recipeHtml, notes, isPublic, categories, avoids, classifications });
      if (newImages.length > 0) {
        const formData = new FormData();
        newImages.forEach(img => formData.append('images', img));
        await recipesAPI.uploadImages(id, formData);
      }
      navigate(`/recipe/${id}`);
    } catch (err) {
      setError(err.message);
      setReviewStatus('idle');
    }
    setSaving(false);
  };

  const getSaveButtonClass = () => {
    switch (reviewStatus) {
      case 'reviewing': return 'btn btn-review-pending btn-lg';
      case 'rejected': return 'btn btn-review-retry btn-lg';
      case 'maxFailed': return 'btn btn-review-maxfailed btn-lg';
      default: return 'btn btn-primary btn-lg';
    }
  };

  const getSaveButtonText = () => {
    if (saving) return 'Saving...';
    switch (reviewStatus) {
      case 'reviewing': return '🔍 Under Review...';
      case 'rejected': return '🔄 Retry Review';
      case 'maxFailed': return '🚫 Maximum Reviews Exceeded';
      default: return 'Save Changes';
    }
  };

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div className="add-recipe-page">
      <div className="add-recipe-card card">
        <h2 style={{ marginBottom: 24 }}>✏️ Edit Recipe</h2>
        {error && <div className="auth-error" style={{ marginBottom: 16 }}>{error}</div>}

        {/* Photo Management */}
        <div className="form-group">
          <label>Photos ({existingImages.length + newImages.length}/3)</label>
          <div className="image-upload-area">
            {existingImages.map((img) => (
              <div key={img.imageId} className="image-preview">
                <img src={img.imageURL} alt={`Recipe ${img.imageId + 1}`} />
                <button className="image-remove" onClick={() => handleDeleteExisting(img.imageId)}>×</button>
              </div>
            ))}
            {newPreviews.map((src, i) => (
              <div key={`new-${i}`} className="image-preview">
                <img src={src} alt={`New ${i + 1}`} />
                <button className="image-remove" onClick={() => removeNewImage(i)}>×</button>
              </div>
            ))}
            {existingImages.length + newImages.length < 3 && (
              <button className="image-add-btn" onClick={() => fileRef.current.click()}>
                <span>+</span>
                <small>Add Photo</small>
              </button>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={handleNewImages} />
        </div>

        <div className="form-group">
          <label>Name ({name.length}/20)</label>
          <input className="input-field" value={name} onChange={e => setName(e.target.value.slice(0, 20))} />
        </div>
        <div className="form-group">
          <label>Description ({description.length}/45)</label>
          <input className="input-field" value={description} onChange={e => setDescription(e.target.value.slice(0, 45))} />
        </div>
        <div className="form-group">
          <label className="toggle-label">
            <span>Public Recipe</span>
            <input type="checkbox" checked={isPublic} onChange={e => setIsPublic(e.target.checked)} />
            <span className="toggle-switch" />
          </label>
        </div>
        <CategoryPicker label="Categories" items={FOOD_CATEGORIES} selected={categories} onToggle={i => toggle(categories, setCategories, i)} icons={CATEGORY_ICONS} />
        <CategoryPicker label="Free From" items={AVOID_CATEGORIES} selected={avoids} onToggle={i => toggle(avoids, setAvoids, i)} />
        <CategoryPicker label="Dietary" items={CLASSIFICATION_CATEGORIES} selected={classifications} onToggle={i => toggle(classifications, setClassifications, i)} />
        <div className="form-group" style={{ marginTop: 16 }}>
          <label>Recipe Instructions</label>
          <RichTextEditor
            value={recipeHtml}
            onChange={setRecipeHtml}
            placeholder="Write your recipe instructions here..."
          />
        </div>
        <div className="form-group">
          <label>Notes</label>
          <textarea className="input-field" value={notes} onChange={e => setNotes(e.target.value)} rows={3} />
        </div>
        <div className="step-actions">
          <button className="btn btn-secondary" onClick={() => navigate(`/recipe/${id}`)}>Cancel</button>
          <button
            className={getSaveButtonClass()}
            onClick={handleSave}
            disabled={saving || reviewStatus === 'reviewing' || reviewStatus === 'maxFailed'}
          >
            {getSaveButtonText()}
          </button>
        </div>
        {reviewFeedback && (
          <div className={`review-feedback ${reviewStatus === 'maxFailed' ? 'review-feedback-error' : 'review-feedback-warning'}`}>
            <strong>📋 AI Review Feedback:</strong> {reviewFeedback}
            {reviewStatus !== 'maxFailed' && (
              <span className="review-attempts"> (Attempt {reviewAttempts}/{MAX_REVIEW_ATTEMPTS})</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
